import Image from 'next/image';
import Head from 'next/head';

import { useRef, useState, useEffect } from 'react';

import { useI18n } from '@lib/i18n';
import { useRouter } from 'next/router';

import style from '@styles/welcome.module.scss';

import {
	HOME_TITLE,
	HOME_DESCRIPTION,
	CDN_URL,
	HOME_LAYOUT,
	HOMEPAGE_VIDEO,
	HIDE_HOMEPAGE_LOGO,
	PROSPECTUS_TITLE,
	HOME_OG_TITLE,
	HOME_OG_DESCRIPTION,
	HOME_OG_IMAGE,
	HP_ANIMATIONS,
	HP_ANIMATION_TYPE,
	MEMBERS_ONLY
} from '@lib/constants';

import { staggerAnimations } from '@lib/helpers';
import { initTriggeredAnimations } from '@lib/helpers/blockAnimations';

import JustRegisteredToast from '@components/CustomToastNotifications/JustRegisteredToast';
import HomepageContent from '@components/HomepageContent';

export default function Home() {
	const { t } = useI18n();
	const router = useRouter();

	const formattedDescription = HOME_DESCRIPTION.replace(/\\n/g, '\n');

	const videoRef = useRef(null);
	const [videoPlaying, setVideoPlaying] = useState(true);

	const animationClass =
		HP_ANIMATIONS && HP_ANIMATION_TYPE ? `animate ${HP_ANIMATION_TYPE} hp-has-triggered-animation` : '';

	const animationData =
		HP_ANIMATIONS && HP_ANIMATION_TYPE
			? {
					'data-hp-trigger-selector': '',
					'data-hp-triggered-animation': HP_ANIMATION_TYPE,
					'data-hp-trigger-event': 'viewport',
					'data-hp-triggered-played': '0',
				}
			: {};

	useEffect(() => {
		staggerAnimations();
		initTriggeredAnimations();
	}, []);

	useEffect(() => {
		const video = videoRef.current;

		if (video) {
			if (videoPlaying) {
				video.play();
			} else {
				video.pause();
			}
		}
	}, [videoPlaying]);

	useEffect(() => {
		const handleKeyDown = (e) => {
			// Ctrl + Shift + C
			if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'c') {
				e.preventDefault();
				router.push('/configure');
			}
		};

		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [router]);

	useEffect(() => {
		if (!router.isReady) {
			return;
		}

		const query = { ...router.query };
		const utmParams = {};
		let hasUtm = false;

		Object.keys(query).forEach((key) => {
			if (key.toLowerCase().startsWith('utm_')) {
				utmParams[key] = query[key];
				delete query[key];
				hasUtm = true;
			}
		});

		if (hasUtm) {
			localStorage.setItem('utm_params', JSON.stringify(utmParams));

			router.replace(
				{
					pathname: router.pathname,
					query: query,
				},
				undefined,
				{ shallow: true }
			);
		}
	}, [router.isReady]);

	return (
		<>
			<Head>
				<title>{t('general.prospectus_title', PROSPECTUS_TITLE)}</title>
				{/* Conditionally render og tags for the homepage */}
				{HOME_OG_TITLE && <meta property="og:title" content={HOME_OG_TITLE} />}
				{HOME_OG_DESCRIPTION && <meta property="og:description" content={HOME_OG_DESCRIPTION} />}
				{HOME_OG_IMAGE && <meta property="og:image" content={CDN_URL + '/homepage-og-image.webp'} />}
			</Head>
			<JustRegisteredToast />
			<div
				className={`columns is-marginless home-layout homeLayout ${style.welcome} ${style[HOME_LAYOUT] ?? ''}`}
			>
				<div className={`column is-6 is-desktop-12 has-text-align-center homeLayout__left ${style.columnLeft}`}>
					{HOMEPAGE_VIDEO && (
						<video ref={videoRef} autoPlay muted loop playsInline>
							<source src={CDN_URL + '/homepage-video.webm'} type="video/webm" />
							<source src={CDN_URL + '/homepage-video.mp4'} type="video/mp4" />
						</video>
					)}

					{HOMEPAGE_VIDEO && (
						<button
							className={`homeLayout__videoControls ${style.videoControls}`}
							title="Toggle video playback"
							onClick={() => setVideoPlaying(!videoPlaying)}
						>
							{videoPlaying ? (
								<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
									<path
										fill="#000000"
										d="M48 64C21.5 64 0 85.5 0 112V400c0 26.5 21.5 48 48 48H80c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48H48zm192 0c-26.5 0-48 21.5-48 48V400c0 26.5 21.5 48 48 48h32c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48H240z"
									/>
								</svg>
							) : (
								<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
									<path
										fill="#000000"
										d="M73 39c-14.8-9.1-33.4-9.4-48.5-.9S0 62.6 0 80V432c0 17.4 9.4 33.4 24.5 41.9s33.7 8.1 48.5-.9L361 297c14.3-8.7 23-24.2 23-41s-8.7-32.2-23-41L73 39z"
									/>
								</svg>
							)}
						</button>
					)}

					<Image src={CDN_URL + '/homepage-image.webp'} priority alt="" fill sizes="100vw" />
				</div>
				<div className={`column is-6 is-desktop-12 is-centered homeLayout__right ${style.columnRight}`}>
					<div className={`${style.innerWrap} has-text-center homeLayout__right-inner`}>
						<HomepageContent
							membersOnly={MEMBERS_ONLY}
							animationClass={animationClass}
							animationData={animationData}
						/>
					</div>
				</div>
			</div>
		</>
	);
}
